import { useCallback, useEffect, useRef, useState } from "react";

const PADDINGS = 110;
const DRAG_THRESHOLD = 5;
// Below this a pixel wheel delta is treated as a trackpad gesture, not a notch.
const WHEEL_STEP_MIN = 50;
const LINE_HEIGHT = 40;
const SMOOTH_FALLBACK_MS = 250;

/**
 * Row browsing: native horizontal scrolling (wheel, trackpad, touch swipe,
 * click-and-drag) with the arrow controls kept as a secondary mechanism.
 * The row is a real scroll container, so no items are re-rendered while
 * browsing and lazy loading / pagination behave as before.
 * Hover scale origin is CSS-only (left center on all cards) — no scroll-edge
 * class marking here.
 */
const useSliding = (elementWidth, countElements) => {
  const containerRef = useRef(null);
  const dragRef = useRef(null);
  const suppressClickRef = useRef(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [hasNext, setHasNext] = useState(false);

  const syncEdges = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setHasPrev(el.scrollLeft > 1);
    setHasNext(max > 1 && el.scrollLeft < max - 1);
  }, []);

  useEffect(() => {
    syncEdges();
  }, [syncEdges, countElements, elementWidth]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    let frame = null;
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        syncEdges();
      });
    };

    // A mouse wheel over a row scrolls the row horizontally; at either end the
    // gesture falls through so the page keeps scrolling. Trackpad gestures are
    // left alone: horizontal ones scroll the row natively, vertical ones scroll
    // the page.
    const onWheel = (event) => {
      if (event.ctrlKey) return;
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
      const lineMode = event.deltaMode !== 0;
      if (!lineMode && Math.abs(event.deltaY) < WHEEL_STEP_MIN) return;
      const delta = lineMode ? event.deltaY * LINE_HEIGHT : event.deltaY;
      if (!delta) return;
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 0) return;
      if (delta < 0 && el.scrollLeft <= 0) return;
      if (delta > 0 && el.scrollLeft >= max - 1) return;
      event.preventDefault();
      el.scrollLeft += delta;
    };

    const onPointerDown = (event) => {
      suppressClickRef.current = false;
      if (event.pointerType === "touch") return;
      if (event.button !== 0) return;
      if (el.scrollWidth - el.clientWidth <= 0) return;
      dragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startScroll: el.scrollLeft,
        moved: false,
      };
    };

    const onPointerMove = (event) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      const dx = event.clientX - drag.startX;
      if (!drag.moved) {
        if (Math.abs(dx) < DRAG_THRESHOLD) return;
        drag.moved = true;
        el.classList.add("slider__container--dragging");
        if (el.setPointerCapture) {
          el.setPointerCapture(event.pointerId);
        }
      }
      el.scrollLeft = drag.startScroll - dx;
    };

    const endDrag = (event) => {
      const drag = dragRef.current;
      if (!drag) return;
      if (event && event.pointerId != null && drag.pointerId !== event.pointerId) return;
      dragRef.current = null;
      el.classList.remove("slider__container--dragging");
      // A drag must not open the tile it finished on.
      suppressClickRef.current = drag.moved;
      if (
        event &&
        el.hasPointerCapture &&
        el.hasPointerCapture(event.pointerId) &&
        el.releasePointerCapture
      ) {
        el.releasePointerCapture(event.pointerId);
      }
    };

    const onClickCapture = (event) => {
      if (!suppressClickRef.current) return;
      suppressClickRef.current = false;
      event.preventDefault();
      event.stopPropagation();
    };

    const onDragStart = (event) => {
      if (dragRef.current) event.preventDefault();
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", endDrag);
    el.addEventListener("pointercancel", endDrag);
    el.addEventListener("click", onClickCapture, true);
    el.addEventListener("dragstart", onDragStart);
    window.addEventListener("resize", syncEdges);

    syncEdges();

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      el.removeEventListener("scroll", onScroll);
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", endDrag);
      el.removeEventListener("pointercancel", endDrag);
      el.removeEventListener("click", onClickCapture, true);
      el.removeEventListener("dragstart", onDragStart);
      window.removeEventListener("resize", syncEdges);
    };
  }, [syncEdges]);

  const scrollByPage = (direction) => {
    const el = containerRef.current;
    if (!el) return;
    const step = Math.max(el.clientWidth - PADDINGS, elementWidth || 0) || el.clientWidth;
    const from = el.scrollLeft;
    const max = el.scrollWidth - el.clientWidth;
    const target = Math.max(0, Math.min(max, from + direction * step));

    if (el.scrollTo) {
      el.scrollTo({ left: target, behavior: "smooth" });
    } else {
      el.scrollLeft = target;
    }

    // Smooth scrolling is a no-op in some embedded browsers; land the step
    // anyway if the animation never started.
    window.setTimeout(function () {
      if (el.scrollLeft === from && target !== from) {
        el.scrollLeft = target;
      }
    }, SMOOTH_FALLBACK_MS);
  };

  const handlePrev = () => scrollByPage(-1);

  const handleNext = () => scrollByPage(1);

  return { handlePrev, handleNext, slideProps: {}, containerRef, hasPrev, hasNext };
};

export default useSliding;
